import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface OfferResponseParams {
  offerType: string;
  screeningId: string;
  userId?: string;
}

// Store outside component to persist across remounts (React Strict Mode)
const processedRequests = new Set<string>();

const OfferResponseMain = () => {
  const { offerType, screeningId, userId } = useParams<OfferResponseParams>();
  const { t } = useTranslation("job-details");
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const hasCalledRef = useRef(false);

  const screeningApi = useScreeningApi();
  const toast = useToaster();
  const history = useHistory();

  useEffect(() => {
    console.log(">>>>>>>>>>>>>> useEffect called", {
      screeningId,
      offerType,
      hasProcessed,
      hasCalledRef: hasCalledRef.current,
    });

    // Use ref to ensure it only runs once, even in Strict Mode
    if (hasCalledRef.current) {
      console.log(">>>>>>>>>>>>>> Already called, skipping");
      return;
    }

    // Auto-process the offer response when component mounts
    if (screeningId && offerType) {
      console.log(">>>>>>>>>>>>>> Calling handleOfferResponse");
      handleOfferResponse();
    }

    // Cleanup function (won't help with double call but good practice)
    return () => {
      console.log(">>>>>>>>>>>>>> useEffect cleanup");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOfferResponse = async () => {
    const requestKey = `${screeningId}-${offerType}`;

    console.log(">>>>>>>>>>>>>> handleOfferResponse called", {
      requestKey,
      alreadyProcessed: processedRequests.has(requestKey),
      hasCalledRef: hasCalledRef.current,
      isProcessing,
      hasProcessed,
    });

    // Check if this specific request has already been processed
    if (processedRequests.has(requestKey)) {
      console.log(">>>>>>>>>>>>>> Request already processed globally, returning");
      return;
    }

    if (hasCalledRef.current) {
      console.log(">>>>>>>>>>>>>> Ref check: Already called, returning");
      return;
    }

    if (isProcessing || hasProcessed) {
      console.log(">>>>>>>>>>>>>> State check: Already processing, returning");
      return;
    }

    console.log(">>>>>>>>>>>>>> Setting hasCalledRef to true and marking request as processed");
    hasCalledRef.current = true;
    processedRequests.add(requestKey);

    const isAccept = offerType === "accept";
    const isReject = offerType === "decline";

    if (!isAccept && !isReject) {
      setErrorMessage(t("jobDetails.offerResponse.errors.invalidType"));
      return;
    }

    setIsProcessing(true);
    setHasProcessed(true);

    const requestData = {
      user_id: userId,
      screening_id: screeningId,
      title: isAccept
        ? t("jobDetails.offerResponse.apiPayload.titleAccepted")
        : t("jobDetails.offerResponse.apiPayload.titleRejected"),
      description: isAccept
        ? t("jobDetails.offerResponse.apiPayload.descriptionAccepted")
        : t("jobDetails.offerResponse.apiPayload.descriptionRejected"),
      application_type: 4, // Application type for offer response
      status: isAccept ? 7 : 8, // Status: 7 for accept, 8 for reject
    };

    screeningApi.callAddFeedback(
      requestData,
      (message: string, response: any) => {
        setIsProcessing(false);
        setIsSuccess(true);
        toast.success(
          isAccept
            ? t("jobDetails.offerResponse.messages.acceptSuccess")
            : t("jobDetails.offerResponse.messages.declineSuccess")
        );
      },
      (error: string) => {
        setIsProcessing(false);
        setErrorMessage(
          isAccept
            ? t("jobDetails.offerResponse.errors.acceptFailed")
            : t("jobDetails.offerResponse.errors.declineFailed")
        );
        toast.error(error);
      }
    );
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const isAccept = offerType === "accept";
  const isReject = offerType === "decline";

  return (
    <div className="offer-response-container">
      <div className="offer-response-card">
        <div className="offer-response-header">
          <div className="mb-3">
            <img src="/images/dr-logo.png" alt="Dr. Job Pro" />
          </div>
          <h1 className="offer-response-title">
            {isAccept
              ? t("jobDetails.offerResponse.title.accept")
              : isReject
              ? t("jobDetails.offerResponse.title.decline")
              : t("jobDetails.offerResponse.title.invalid")}
          </h1>
        </div>

        <div className="offer-response-content">
          {isProcessing && (
            <div className="processing-state">
              <div className="loading-spinner"></div>
              <h2>{t("jobDetails.offerResponse.processing.title")}</h2>
              <p>{t("jobDetails.offerResponse.processing.description")}</p>
            </div>
          )}

          {!isProcessing && isSuccess && (
            <div className="success-state">
              <div className="success-icon">
                <i className="fa fa-check-circle"></i>
              </div>
              <h2>
                {isAccept
                  ? t("jobDetails.offerResponse.success.titleAccept")
                  : t("jobDetails.offerResponse.success.titleDecline")}
              </h2>
              <p>
                {isAccept
                  ? t("jobDetails.offerResponse.success.descriptionAccept")
                  : t("jobDetails.offerResponse.success.descriptionDecline")}
              </p>
              <button className="btn-preview" onClick={handleGoHome}>
                {t("jobDetails.offerResponse.actions.visitSite")}
              </button>
            </div>
          )}

          {!isProcessing && !isSuccess && errorMessage && (
            <div className="error-state">
              <div className="error-icon">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <h2>{t("jobDetails.offerResponse.error.title")}</h2>
              <p>{errorMessage}</p>
              <div className="error-actions">
                <button className="btn-secondary" onClick={() => window.location.reload()}>
                  {t("jobDetails.offerResponse.actions.tryAgain")}
                </button>
                <button className="btn-primary" onClick={handleGoHome}>
                  {t("jobDetails.offerResponse.actions.goToSite")}
                </button>
              </div>
            </div>
          )}

          {!isAccept && !isReject && !isProcessing && (
            <div className="error-state">
              <div className="error-icon">
                <i className="fa fa-question-circle"></i>
              </div>
              <h2>{t("jobDetails.offerResponse.invalidLink.title")}</h2>
              <p>{t("jobDetails.offerResponse.invalidLink.description")}</p>
              <button className="btn-preview" onClick={handleGoHome}>
                {t("jobDetails.offerResponse.actions.goToSite")}
              </button>
            </div>
          )}
        </div>

        <div className="offer-response-footer">
          <p>
            <i className="fa fa-shield"></i>
            {t("jobDetails.offerResponse.footer.secureMessage")}
          </p>
          <p>
            {t("jobDetails.offerResponse.footer.needHelp")}{" "}
            <a href="mailto:support@drjobpro.com">support@drjobpro.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OfferResponseMain;
